/**
 * Deep Mine: the rules both the server and the client need. The server deals
 * every floor and decides every dig; the client only draws what it's told.
 *
 * Difficulty comes from three limits working against each other: lantern energy
 * (every dig costs some), rock hardness (each zone needs a stronger pickaxe) and
 * backpack space. Deeper zones have harder rock, gas pockets, falling rocks and
 * lava. Running out of energy means Pip the canary leads you out, but you drop
 * half of what you were carrying.
 */

export type RockKey = 'dirt' | 'stone' | 'granite' | 'crystal' | 'basalt' | 'voidstone' | 'starrock';
export const ROCKS: Record<RockKey, { name: string; hardness: number; color: string }> = {
  dirt: { name: 'Dirt', hardness: 1, color: '#9b7650' },
  stone: { name: 'Stone', hardness: 2, color: '#8b8f96' },
  granite: { name: 'Granite', hardness: 3, color: '#7d6a6f' },
  crystal: { name: 'Crystal rock', hardness: 4, color: '#7e8fc4' },
  basalt: { name: 'Basalt', hardness: 5, color: '#4b4650' },
  voidstone: { name: 'Voidstone', hardness: 6, color: '#3a2f55' },
  starrock: { name: 'Star rock', hardness: 7, color: '#2b3a5c' },
};

export type OreKey = 'coal' | 'copper' | 'iron' | 'silver' | 'gold' | 'emerald' | 'sapphire' | 'ruby' | 'diamond' | 'starmetal';
/** sell: coins each at the Assay Office · xp: mining XP when dug up. */
export const ORES: Record<OreKey, { name: string; sell: number; xp: number; color: string; minFloor: number }> = {
  coal: { name: 'Coal', sell: 3, xp: 2, color: '#2d2d2d', minFloor: 1 },
  copper: { name: 'Copper', sell: 6, xp: 3, color: '#c8743a', minFloor: 1 },
  iron: { name: 'Iron', sell: 12, xp: 5, color: '#b9b2a8', minFloor: 5 },
  silver: { name: 'Silver', sell: 18, xp: 7, color: '#d8dde3', minFloor: 12 },
  gold: { name: 'Gold', sell: 30, xp: 10, color: '#f2c230', minFloor: 20 },
  emerald: { name: 'Emerald', sell: 45, xp: 14, color: '#2fbf71', minFloor: 30 },
  sapphire: { name: 'Sapphire', sell: 65, xp: 18, color: '#3a7de0', minFloor: 42 },
  ruby: { name: 'Ruby', sell: 85, xp: 23, color: '#e0344f', minFloor: 55 },
  diamond: { name: 'Diamond', sell: 140, xp: 32, color: '#9ef0ff', minFloor: 70 },
  starmetal: { name: 'Starmetal', sell: 220, xp: 45, color: '#ffe680', minFloor: 101 },
};
export const ORE_KEYS = Object.keys(ORES) as OreKey[];
export const oreItemKey = (o: OreKey) => `ore_${o}`;

export type ArtifactKey = 'shell_fossil' | 'old_lantern' | 'captains_compass' | 'dino_fossil' | 'mermaid_comb' | 'glow_crystal' | 'ancient_coin' | 'dragon_scale' | 'star_shard';
export const ARTIFACTS: Record<ArtifactKey, { name: string; description: string; minFloor: number }> = {
  shell_fossil: { name: 'Shell Fossil', description: 'A sea shell turned to stone long, long ago.', minFloor: 3 },
  old_lantern: { name: 'Old Miner’s Lantern', description: 'Someone dug here before you…', minFloor: 8 },
  captains_compass: { name: 'Captain’s Compass', description: 'It always points to the sea.', minFloor: 15 },
  dino_fossil: { name: 'Dino Fossil', description: 'A tiny dinosaur footprint.', minFloor: 25 },
  mermaid_comb: { name: 'Mermaid’s Comb', description: 'Pearly and impossibly smooth.', minFloor: 35 },
  glow_crystal: { name: 'Glow Crystal', description: 'Hums softly in the dark.', minFloor: 45 },
  ancient_coin: { name: 'Ancient Coin', description: 'Nobody can read the writing on it.', minFloor: 60 },
  dragon_scale: { name: 'Dragon Scale', description: 'Still warm from the Magma Halls.', minFloor: 70 },
  star_shard: { name: 'Star Shard', description: 'A piece of a fallen star from the Core.', minFloor: 101 },
};
export const ARTIFACT_KEYS = Object.keys(ARTIFACTS) as ArtifactKey[];
export const artifactItemKey = (a: ArtifactKey) => `artifact_${a}`;

export interface MineZone {
  key: string;
  name: string;
  from: number;
  to: number;
  rocks: RockKey[];
  w: number;
  h: number;
  /** Chance per buried square of being ore. */
  oreRate: number;
  gas: number;
  rockfall: number;
  lava: number;
  color: string;
}
/** 120 floors in seven zones. Each new zone needs a stronger pickaxe. */
export const MINE_ZONES: MineZone[] = [
  { key: 'sandy', name: 'Sandy Tunnels', from: 1, to: 10, rocks: ['dirt', 'stone'], w: 12, h: 10, oreRate: 0.11, gas: 0, rockfall: 0, lava: 0, color: '#c9a46b' },
  { key: 'caverns', name: 'Stone Caverns', from: 11, to: 25, rocks: ['stone', 'granite'], w: 13, h: 11, oreRate: 0.1, gas: 0.01, rockfall: 0.02, lava: 0, color: '#8b8f96' },
  { key: 'iron', name: 'Iron Depths', from: 26, to: 40, rocks: ['granite'], w: 14, h: 11, oreRate: 0.1, gas: 0.02, rockfall: 0.03, lava: 0, color: '#7d6a6f' },
  { key: 'grottos', name: 'Crystal Grottos', from: 41, to: 60, rocks: ['granite', 'crystal'], w: 15, h: 12, oreRate: 0.09, gas: 0.025, rockfall: 0.035, lava: 0, color: '#7e8fc4' },
  { key: 'magma', name: 'Magma Halls', from: 61, to: 80, rocks: ['crystal', 'basalt'], w: 16, h: 12, oreRate: 0.09, gas: 0.03, rockfall: 0.04, lava: 0.05, color: '#b8452e' },
  { key: 'abyss', name: 'The Abyss', from: 81, to: 100, rocks: ['basalt', 'voidstone'], w: 17, h: 13, oreRate: 0.085, gas: 0.035, rockfall: 0.05, lava: 0.06, color: '#3a2f55' },
  { key: 'core', name: 'Starfall Core', from: 101, to: 120, rocks: ['voidstone', 'starrock'], w: 18, h: 13, oreRate: 0.08, gas: 0.04, rockfall: 0.06, lava: 0.07, color: '#2b3a5c' },
];
export const MINE_MAX_FLOOR = 120;
export const zoneFor = (floor: number) => MINE_ZONES.find((z) => floor >= z.from && floor <= z.to) ?? MINE_ZONES[MINE_ZONES.length - 1]!;
/** Hardest rock in a zone: the pickaxe you need to get through it. */
export const zoneHardness = (z: MineZone) => Math.max(...z.rocks.map((r) => ROCKS[r].hardness));
/** Camps you can start a dive from once you've reached them: 1, 6, 11 … 116. */
export const campFor = (floor: number) => Math.floor((Math.max(1, floor) - 1) / 5) * 5 + 1;

export type Cost = { coins: number; materials: Partial<Record<OreKey, number>> };

export type PickaxeKey = 'wooden' | 'copper' | 'iron' | 'steel' | 'gold' | 'emerald' | 'ruby' | 'diamond' | 'starmetal';
/**
 * power: hardest rock it breaks · efficiency: share of the dig cost (and dig time) it pays ·
 * level: Mining level needed · durability: wear it takes before it breaks (each dig wears it
 * by the rock's hardness).
 */
export const PICKAXES: Record<PickaxeKey, { name: string; power: number; efficiency: number; level: number; durability: number; cost: Cost; color: string }> = {
  wooden: { name: 'Wooden Pickaxe', power: 2, efficiency: 1, level: 0, durability: 160, cost: { coins: 0, materials: {} }, color: '#a0763f' },
  copper: { name: 'Copper Pickaxe', power: 2, efficiency: 0.8, level: 4, durability: 260, cost: { coins: 250, materials: { copper: 20, coal: 10 } }, color: '#c8743a' },
  iron: { name: 'Iron Pickaxe', power: 3, efficiency: 0.8, level: 10, durability: 420, cost: { coins: 700, materials: { iron: 25, coal: 20 } }, color: '#b9b2a8' },
  steel: { name: 'Steel Pickaxe', power: 3, efficiency: 0.65, level: 18, durability: 650, cost: { coins: 1400, materials: { iron: 40, coal: 40, silver: 10 } }, color: '#8fa3b8' },
  // Gold is soft: strong enough for crystal rock, but it wears quickly.
  gold: { name: 'Golden Pickaxe', power: 4, efficiency: 0.6, level: 28, durability: 520, cost: { coins: 2500, materials: { gold: 25, silver: 20 } }, color: '#f2c230' },
  emerald: { name: 'Emerald Pickaxe', power: 4, efficiency: 0.5, level: 38, durability: 900, cost: { coins: 4000, materials: { emerald: 20, gold: 20 } }, color: '#2fbf71' },
  ruby: { name: 'Ruby Pickaxe', power: 5, efficiency: 0.45, level: 52, durability: 1100, cost: { coins: 6500, materials: { ruby: 20, sapphire: 15 } }, color: '#e0344f' },
  diamond: { name: 'Diamond Pickaxe', power: 6, efficiency: 0.4, level: 68, durability: 1600, cost: { coins: 10000, materials: { diamond: 15, ruby: 20 } }, color: '#9ef0ff' },
  starmetal: { name: 'Starmetal Pickaxe', power: 7, efficiency: 0.3, level: 85, durability: 2200, cost: { coins: 16000, materials: { starmetal: 15, diamond: 15 } }, color: '#ffe680' },
};
export const PICKAXE_KEYS = Object.keys(PICKAXES) as PickaxeKey[];

/** Upgrades bought in order, level 1 first. Index 0 is what everyone starts with. */
export const LANTERNS: { energy: number; light: number; level: number; cost: Cost }[] = [
  { energy: 100, light: 1, level: 0, cost: { coins: 0, materials: {} } },
  { energy: 130, light: 1, level: 3, cost: { coins: 200, materials: { coal: 15, copper: 10 } } },
  { energy: 165, light: 2, level: 8, cost: { coins: 500, materials: { iron: 15, coal: 20 } } },
  { energy: 205, light: 2, level: 15, cost: { coins: 1000, materials: { silver: 15, iron: 20 } } },
  { energy: 250, light: 2, level: 25, cost: { coins: 2000, materials: { gold: 15, silver: 15 } } },
  { energy: 300, light: 3, level: 40, cost: { coins: 3500, materials: { sapphire: 10, emerald: 10 } } },
  { energy: 360, light: 3, level: 58, cost: { coins: 6000, materials: { ruby: 12, sapphire: 12 } } },
  { energy: 420, light: 3, level: 75, cost: { coins: 9000, materials: { diamond: 10, ruby: 12 } } },
];
export const BACKPACKS: { slots: number; level: number; cost: Cost }[] = [
  { slots: 15, level: 0, cost: { coins: 0, materials: {} } },
  { slots: 22, level: 2, cost: { coins: 150, materials: { copper: 10 } } },
  { slots: 30, level: 7, cost: { coins: 400, materials: { iron: 15 } } },
  { slots: 40, level: 14, cost: { coins: 900, materials: { silver: 12, iron: 10 } } },
  { slots: 52, level: 24, cost: { coins: 1800, materials: { gold: 12 } } },
  { slots: 65, level: 36, cost: { coins: 3200, materials: { emerald: 12 } } },
  { slots: 80, level: 55, cost: { coins: 5500, materials: { ruby: 10, sapphire: 10 } } },
];
/** Each helmet level takes 20% off gas and falling-rock energy loss. */
export const HELMETS: { protection: number; level: number; cost: Cost }[] = [
  { protection: 0, level: 0, cost: { coins: 0, materials: {} } },
  { protection: 0.2, level: 6, cost: { coins: 300, materials: { iron: 10, copper: 10 } } },
  { protection: 0.4, level: 20, cost: { coins: 1200, materials: { silver: 15, iron: 15 } } },
  { protection: 0.6, level: 35, cost: { coins: 3000, materials: { gold: 15, emerald: 8 } } },
  { protection: 0.8, level: 60, cost: { coins: 7000, materials: { ruby: 10, diamond: 5 } } },
];
export type GearSlot = 'lantern' | 'backpack' | 'helmet';
export const GEAR: Record<GearSlot, { name: string; levels: { level: number; cost: Cost }[] }> = {
  lantern: { name: 'Lantern', levels: LANTERNS },
  backpack: { name: 'Backpack', levels: BACKPACKS },
  helmet: { name: 'Mining Helmet', levels: HELMETS },
};

export interface MineGear {
  pickaxe: PickaxeKey;
  lantern: number;
  backpack: number;
  helmet: number;
  /** Wear left on the pickaxe; missing means brand new. */
  durability?: number;
  /** Times this pickaxe has been repaired; each repair takes 10% off its most durability. */
  repairs?: number;
}
export const STARTER_GEAR: MineGear = { pickaxe: 'wooden', lantern: 0, backpack: 0, helmet: 0, durability: PICKAXES.wooden.durability, repairs: 0 };

// ---------------------------------------------------------------------------
// Pickaxe wear, repairs and how long each dig takes
// ---------------------------------------------------------------------------

/** Repairs never take a pickaxe below half its new durability. */
export const MAX_REPAIR_LOSS = 0.5;
export const REPAIR_LOSS = 0.1;
/** Most durability the pickaxe can have now, after its repairs. */
export const maxDurability = (g: Pick<MineGear, 'pickaxe' | 'repairs'>) =>
  Math.round(PICKAXES[g.pickaxe].durability * (1 - Math.min(MAX_REPAIR_LOSS, REPAIR_LOSS * (g.repairs ?? 0))));
export const durabilityOf = (g: MineGear) => Math.min(g.durability ?? maxDurability(g), maxDurability(g));
/** Each dig wears the pickaxe by the rock's hardness (crystal and harder wear it faster still). */
export const pickaxeWear = (hardness: number) => (hardness >= 4 ? hardness + 1 : hardness);
/** A pickaxe below this share of its durability shows a warning. */
export const WORN_SHARE = 0.25;

/**
 * Repairing at the Forge: coins and a few of the pickaxe's own ores, in proportion to the
 * wear. The Wooden Pickaxe is always repaired for free, so nobody gets stuck.
 */
export function repairCost(g: MineGear): Cost {
  const max = maxDurability(g);
  const missing = Math.max(0, max - durabilityOf(g));
  if (!missing || g.pickaxe === 'wooden') return { coins: 0, materials: {} };
  const share = missing / max;
  const p = PICKAXES[g.pickaxe];
  const materials: Partial<Record<OreKey, number>> = {};
  for (const [ore, n] of Object.entries(p.cost.materials) as [OreKey, number][]) materials[ore] = Math.max(1, Math.ceil(n * 0.25 * share));
  return { coins: Math.max(10, Math.ceil(p.cost.coins * 0.25 * share)), materials };
}

/**
 * How long the dig button must be held, in milliseconds. Harder rock takes longer, better
 * pickaxes are quicker, and squares with something in them take a little extra care.
 */
export function digTimeMs(cell: { rock: RockKey; ore?: OreKey; artifact?: boolean; key?: boolean }, pickaxe: PickaxeKey) {
  const hardness = ROCKS[cell.rock].hardness;
  const p = PICKAXES[pickaxe];
  // A pickaxe stronger than the rock it's breaking digs faster.
  const spare = Math.max(0, p.power - hardness);
  let ms = (350 + 420 * hardness) * (0.55 + 0.45 * p.efficiency) * (1 - 0.12 * Math.min(3, spare));
  if (cell.ore) ms += 120 + ORES[cell.ore].xp * 14;
  if (cell.artifact) ms += 900;
  if (cell.key) ms += 400;
  return Math.round(ms);
}
/** The server allows this much less than the full hold, for network delay. */
export const DIG_TIME_GRACE = 0.8;

/** Energy to dig one square: harder rock costs more, better pickaxes pay less of it. */
export const digCost = (hardness: number, pickaxe: PickaxeKey) => Math.max(1, Math.round(hardness * 1.5 * PICKAXES[pickaxe].efficiency));
export const DESCEND_COST = 3;
/** Energy for a dive: lantern, plus half a point per Mining level. */
export const diveEnergy = (gear: MineGear, miningLevel: number) => LANTERNS[gear.lantern]!.energy + Math.floor(miningLevel / 2);

// ---------------------------------------------------------------------------
// Skills (Mining first; Fishing and Farming can use the same levels later)
// ---------------------------------------------------------------------------

export const SKILL_MAX_LEVEL = 100;
/** Total XP needed to reach a level: level 10 = 1,000 · level 50 = 25,000 · level 100 = 100,000. */
export const skillXpFor = (level: number) => (level <= 0 ? 0 : 10 * level * level);
export function skillLevel(xp: number) {
  let level = 0;
  while (level < SKILL_MAX_LEVEL && xp >= skillXpFor(level + 1)) level++;
  return level;
}
/** The Assay Office pays out up to this many coins a day, growing with Mining level. */
export const assayDailyLimit = (miningLevel: number) => 400 + 40 * miningLevel;

// ---------------------------------------------------------------------------
// What the client sees
// ---------------------------------------------------------------------------

/** One square as the player sees it. Unseen squares are null. */
export type MineCellDto =
  | { t: 'open' }
  | { t: 'ladder' }
  | { t: 'chest' }
  | { t: 'lava' }
  | { t: 'wall' }
  | { t: 'rock'; rock: RockKey; ore?: OreKey; artifact?: true; key?: true };

export interface MineDiveDto {
  floor: number;
  zone: string;
  w: number;
  h: number;
  cells: (MineCellDto | null)[];
  pos: number;
  energy: number;
  maxEnergy: number;
  haul: Partial<Record<OreKey | ArtifactKey, number>>;
  carried: number;
  slots: number;
  hasKey: boolean;
  durability: number;
  maxDurability: number;
}

export interface MineStatusDto {
  level: number;
  xp: number;
  levelXp: number;
  nextLevelXp: number | null;
  deepest: number;
  camps: number[];
  gear: MineGear;
  durability: number;
  maxDurability: number;
  repair: Cost;
  energy: number;
  dive: MineDiveDto | null;
  ores: Partial<Record<OreKey, number>>;
  artifacts: ArtifactKey[];
  assay: { soldToday: number; dailyLimit: number };
}
